import React from 'react';
import { Phone, Mail, Clock, FileText } from 'lucide-react';

// Reference image import (not used directly in layout as we are rebuilding it)
// import exampleImage from 'figma:asset/6a229dd26eed99c0c5f418223b4b5b5f751b39b4.png';

export const Contact = () => {
  return (
    <div className="bg-[#F9FAFB] min-h-screen py-12 px-4 md:px-8 font-sans">
      <div className="container mx-auto max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Sidebar: Contact Info */}
          <div className="lg:col-span-4 bg-white border-l-4 border-[#459EDF] p-8 shadow-sm rounded-r-xl sticky top-24">
            <div className="flex items-center gap-3 mb-8">
              <Phone className="w-6 h-6 text-[#FF7F32]" />
              <h2 className="text-xl font-black italic text-[#061970] uppercase tracking-wide">CONTÁCTANOS</h2>
            </div>
            
            <div className="space-y-8">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">SOPORTE TELEFÓNICO</p>
                <a href="tel:+525563168297" className="text-[#061970] font-black text-lg hover:underline decoration-2 underline-offset-4 decoration-[#FF7F32]">
                  +52 55 6316 8297
                </a>
              </div>
              
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">CORREO ELECTRÓNICO</p>
                <a href="mailto:contacto@bewell.mx" className="text-[#061970] font-black text-lg hover:text-[#FF7F32] transition-colors">
                  contacto@bewell.mx
                </a>
              </div>
              
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">HORARIO</p>
                <div className="flex gap-3">
                  <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="text-sm font-medium text-gray-600 space-y-1">
                    <p>Lun-Vie: 9am - 6pm CST</p>
                    <p>Sáb: 10am - 2pm CST</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Content */}
          <div className="lg:col-span-8 space-y-12">
            
            {/* Warranty Section */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#FF7F32]/10 rounded-full flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#FF7F32]" />
                </div>
                <h2 className="text-2xl font-black italic text-[#061970] uppercase tracking-tighter">INFORMACIÓN DE GARANTÍA</h2>
              </div>
              
              <p className="text-gray-600 mb-8 leading-relaxed">
                Todos los cascos MRC vienen con una <strong className="text-gray-900">garantía limitada de 1 mes</strong> contra defectos de materiales y mano de obra. Esta garantía es válida solo para el comprador original y aplica desde la fecha de compra en la misma tienda donde se adquirió el producto.
              </p>
              
              <div className="border border-gray-200 rounded-xl p-8 bg-white">
                <div className="mb-8">
                  <h3 className="font-black text-[#061970] uppercase text-sm tracking-wide mb-4">QUÉ CUBRE:</h3>
                  <ul className="space-y-3 text-gray-600 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="block w-1.5 h-1.5 bg-[#459EDF] rounded-full mt-1.5"></span>
                      Defectos de fabricación en la estructura de la carcasa.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="block w-1.5 h-1.5 bg-[#459EDF] rounded-full mt-1.5"></span>
                      Mecanismos de visor defectuosos (excluyendo desgaste).
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="block w-1.5 h-1.5 bg-[#459EDF] rounded-full mt-1.5"></span>
                      Fallo de la correa o mal funcionamiento de la hebilla.
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-black text-[#061970] uppercase text-sm tracking-wide mb-4">QUÉ NO CUBRE:</h3>
                  <ul className="space-y-3 text-gray-600 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="block w-1.5 h-1.5 bg-gray-300 rounded-full mt-1.5"></span>
                      Daños causados por accidentes o impactos (los cascos son dispositivos de seguridad de un solo uso).
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="block w-1.5 h-1.5 bg-gray-300 rounded-full mt-1.5"></span>
                      Desgaste normal (rasguños, decoloración interior).
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="block w-1.5 h-1.5 bg-gray-300 rounded-full mt-1.5"></span>
                      Modificaciones o pintura inadecuada.
                    </li>
                  </ul>
                </div>
              </div>
            </section>
            
            {/* FAQ Section */}
            <section>
              <h2 className="text-2xl font-black italic text-[#061970] uppercase tracking-tighter mb-8 pb-4 border-b border-gray-100">
                PREGUNTAS FRECUENTES
              </h2>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-[#061970] font-black text-lg mb-2">¿Cómo sé si mi casco me queda bien?</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    Un casco debe sentirse ajustado alrededor de toda la cabeza sin puntos de presión. No debe girar libremente cuando mueves la cabeza. Revisa nuestra Guía de Tallas en cualquier página de producto.
                  </p>
                </div>
                
                <div className="border-t border-gray-100 pt-8">
                  <h3 className="text-[#061970] font-black text-lg mb-2">¿Cuándo debo reemplazar mi casco?</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    Recomendamos reemplazar tu casco cada 3 años, o inmediatamente después de cualquier impacto/choque, incluso si no hay daños visibles.
                  </p>
                </div>
                
                <div className="border-t border-gray-100 pt-8">
                  <h3 className="text-[#061970] font-black text-lg mb-2">¿Puedo lavar el forro interior?</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    Sí, la mayoría de los cascos MRC cuentan con forros extraíbles y lavables. Lavar a mano con jabón suave y secar al aire. No usar secadora.
                  </p>
                </div>
              </div>
            </section>
            
          </div>
        </div>
      </div>
    </div>
  );
};
